import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format, isSameDay } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';

// Formats a { from, to } range for the trigger's own label — deliberately
// short (day + short month, year only when it isn't the current one) since
// this sits inline next to a SearchInput in a filter row, not in its own
// form field.
const formatRange = (range) => {
  if (!range?.from) return null;
  const withYear = (d) =>
    d.getFullYear() === new Date().getFullYear() ? 'd MMM' : 'd MMM yyyy';
  const from = format(range.from, withYear(range.from));
  if (!range.to || isSameDay(range.from, range.to)) return from;
  return `${from} – ${format(range.to, withYear(range.to))}`;
};

/**
 * A single-trigger date-range filter — Popover-anchored two-month Calendar
 * in range mode, styled to sit flush next to SearchInput in a filter row.
 * Controlled: pass `value` ({ from, to } | undefined) and `onChange`.
 */
const DateRangePicker = ({
  value,
  onChange,
  placeholder,
  className,
  align,
}) => {
  const [open, setOpen] = useState(false);
  const label = formatRange(value);

  const handleSelect = (range) => {
    onChange(range);
    if (range?.from && range?.to) setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex w-full items-center gap-2 rounded-lg border border-input bg-card py-2 pl-3 pr-3 text-[12.5px] text-foreground-strong outline-none transition-[border-color,box-shadow] hover:border-primary/50 focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]',
              label && 'pr-8'
            )}
          >
            <CalendarIcon
              className="h-[15px] w-[15px] shrink-0 text-muted-foreground"
              strokeWidth={1.8}
            />
            <span className={cn('truncate', !label && 'text-muted-foreground')}>
              {label || placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align}>
          <Calendar
            mode="range"
            numberOfMonths={2}
            defaultMonth={value?.from}
            selected={value}
            onSelect={handleSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {label && (
        <button
          type="button"
          aria-label="Clear date range"
          onClick={handleClear}
          className="absolute right-2 top-1/2 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border hover:text-foreground-strong"
        >
          <X className="h-[11px] w-[11px]" strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  align: PropTypes.oneOf(['start', 'center', 'end']),
};

DateRangePicker.defaultProps = {
  value: undefined,
  placeholder: 'Filter by date range',
  className: '',
  align: 'end',
};

export default DateRangePicker;
